import { API_BASE_URL } from "@/config/api";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { useEffect, useState } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { Layout } from "./components/Layout";
import { PrivateRoute } from "./components/PrivateRoute";
import { Button } from "./components/ui/button";
import { AuthProvider } from "./contexts/AuthContext";
import { ReportProvider } from "./contexts/ReportContext";
import Admin from "./pages/Admin";
import ApiTokens from "./pages/ApiTokens";
import Dashboard from "./pages/Dashboard";
import Debug from "./pages/Debug";
import EmailTemplates from "./pages/EmailTemplates";
import Index from "./pages/Index";
import Login from "./pages/Login";
import OAuthTest from "./pages/OAuthTest";
import Reports from "./pages/Reports";
import { AlertTriangle, ClipboardList, Loader2, RefreshCw } from "lucide-react";

// Create React Query client
const queryClient = new QueryClient();

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    // Check if backend is available
    const checkBackend = async () => {
      try {
        const controller = new AbortController();
        timeoutId = setTimeout(() => {
          controller.abort();
        }, 10000);

        const response = await fetch(`${API_BASE_URL}/health`, {
          signal: controller.signal,
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
          },
          mode: "cors",
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Health check successful:", data);
        if (isMounted) {
          setIsLoading(false);
        }
      } catch (error: any) {
        console.error("Health check failed:", error);
        clearTimeout(timeoutId);

        if (error.name === "AbortError") {
          if (retryCount < MAX_RETRIES) {
            if (isMounted) {
              setRetryCount((prev) => prev + 1);
            }
            setTimeout(checkBackend, 2000);
          } else {
            if (isMounted) {
              setError(
                "Server connection timed out. Please check if the server is running.",
              );
              setIsLoading(false);
            }
          }
        } else {
          if (isMounted) {
            setError(`Failed to connect to server: ${error.message}`);
            setIsLoading(false);
          }
        }
      }
    };

    checkBackend();

    // Cleanup function
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [retryCount]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 mx-auto shadow-lg shadow-purple-500/25">
            <ClipboardList className="w-8 h-8 text-white" />
          </div>
          <div className="space-y-2">
            <Loader2 className="h-6 w-6 animate-spin text-purple-500 mx-auto" />
            <p className="text-lg font-medium text-foreground">
              Loading Weekly Report
            </p>
            {retryCount > 0 && (
              <p className="text-sm text-muted-foreground">
                Retrying connection ({retryCount}/{MAX_RETRIES})...
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md space-y-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-500/10 mx-auto">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-foreground">
              Connection Error
            </h1>
            <p className="text-muted-foreground">{error}</p>
          </div>
          <Button
            onClick={() => {
              setError(null);
              setIsLoading(true);
              setRetryCount(0);
            }}
            className="gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
          <GoogleOAuthProvider clientId={import.meta.env.GOOGLE_CLIENT_ID}>
            <AuthProvider>
              <ReportProvider>
                <Layout>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/" element={<Index />} />
                    <Route
                      path="/dashboard"
                      element={
                        <PrivateRoute>
                          <Dashboard />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <PrivateRoute>
                          <Admin />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/debug"
                      element={
                        <PrivateRoute>
                          <Debug />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/email-templates"
                      element={
                        <PrivateRoute>
                          <EmailTemplates />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <PrivateRoute>
                          <Reports />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/api-tokens"
                      element={
                        <PrivateRoute>
                          <ApiTokens />
                        </PrivateRoute>
                      }
                    />
                    <Route path="/oauth-test" element={<OAuthTest />} />
                  </Routes>
                </Layout>
                <Toaster
                  theme="system"
                  position="bottom-right"
                  toastOptions={{
                    className: "border border-border",
                  }}
                />
              </ReportProvider>
            </AuthProvider>
          </GoogleOAuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Router>
  );
}
